import { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  ShieldAlert,
  ShieldOff,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  PlayCircle,
  LayoutDashboard,
  FileDown,
  Phone,
  Activity,
  Zap,
  XOctagon,
  Globe,
  Wifi,
  WifiOff,
  AlertTriangle,
  Minus,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SimpleModeProps {
  onSwitchToAdvanced?: () => void;
}

type OverallStatus = 'all-clear' | 'attention' | 'critical';

interface MetricCard {
  label: string;
  value: string;
  subtitle: string;
  trend?: 'up' | 'down' | 'neutral';
  trendLabel?: string;
  type: 'number' | 'score';
  scoreValue?: number; // 0-100 for the circular gauge
}

interface ActivityEvent {
  id: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
}

interface ConnectorDot {
  name: string;
  status: 'connected' | 'warning' | 'disconnected';
}

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const MOCK_STATUS: OverallStatus = 'all-clear';

const MOCK_METRICS: MetricCard[] = [
  {
    label: 'Threats Blocked Today',
    value: '47',
    subtitle: 'Automatically stopped before reaching your network',
    trend: 'down',
    trendLabel: '12% fewer than yesterday',
    type: 'number',
  },
  {
    label: 'Alerts Auto-Resolved',
    value: '38',
    subtitle: '81% of all alerts handled without human help',
    trend: 'up',
    trendLabel: '81% resolution rate',
    type: 'number',
  },
  {
    label: 'Average Response Time',
    value: '2.3 min',
    subtitle: 'Industry average is 24 minutes',
    trend: 'down',
    trendLabel: '10x faster than average',
    type: 'number',
  },
  {
    label: 'Security Score',
    value: '87',
    subtitle: 'Your overall security health rating',
    type: 'score',
    scoreValue: 87,
  },
];

const MOCK_ACTIVITY: ActivityEvent[] = [
  {
    id: '1',
    message: 'Suspicious login attempt from an unrecognized location was blocked',
    severity: 'high',
    timestamp: '2 minutes ago',
  },
  {
    id: '2',
    message: 'Phishing email targeting the finance team was quarantined',
    severity: 'critical',
    timestamp: '14 minutes ago',
  },
  {
    id: '3',
    message: 'Unusual file download on a marketing laptop was flagged and contained',
    severity: 'medium',
    timestamp: '37 minutes ago',
  },
  {
    id: '4',
    message: 'Brute-force password attack on VPN gateway was automatically mitigated',
    severity: 'high',
    timestamp: '1 hour ago',
  },
  {
    id: '5',
    message: 'Software update compliance check completed -- all devices up to date',
    severity: 'low',
    timestamp: '2 hours ago',
  },
  {
    id: '6',
    message: 'Outbound data transfer spike reviewed and cleared as legitimate backup',
    severity: 'medium',
    timestamp: '3 hours ago',
  },
];

const MOCK_CONNECTORS: ConnectorDot[] = [
  { name: 'CrowdStrike', status: 'connected' },
  { name: 'Splunk SIEM', status: 'connected' },
  { name: 'Okta SSO', status: 'connected' },
  { name: 'Microsoft Sentinel', status: 'connected' },
  { name: 'AWS GuardDuty', status: 'connected' },
  { name: 'Jira Tickets', status: 'connected' },
  { name: 'Slack Alerts', status: 'connected' },
  { name: 'PagerDuty', status: 'connected' },
  { name: 'Azure Security', status: 'warning' },
  { name: 'GCP SCC', status: 'warning' },
  { name: 'Tenable.io', status: 'disconnected' },
  { name: 'Carbon Black', status: 'disconnected' },
];

// ---------------------------------------------------------------------------
// Helper: tiny circular progress (SVG)
// ---------------------------------------------------------------------------

function CircularScore({ value, size = 120 }: { value: number; size?: number }) {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const [animatedOffset, setAnimatedOffset] = useState(circumference);

  useEffect(() => {
    // Small delay so the animation plays after mount
    const timeout = setTimeout(() => {
      const progress = Math.min(Math.max(value, 0), 100) / 100;
      setAnimatedOffset(circumference - progress * circumference);
    }, 300);
    return () => clearTimeout(timeout);
  }, [value, circumference]);

  const scoreColor =
    value >= 80 ? 'text-success-400' : value >= 60 ? 'text-warning-400' : 'text-danger-400';
  const strokeColor = value >= 80 ? '#34D399' : value >= 60 ? '#FBBF24' : '#F87171';

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          className="text-slate-700/50"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={animatedOffset}
          style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${scoreColor}`}>{value}</span>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider">/ 100</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper: severity dot color
// ---------------------------------------------------------------------------

function severityDotClass(severity: ActivityEvent['severity']): string {
  switch (severity) {
    case 'critical':
      return 'bg-danger-400';
    case 'high':
      return 'bg-orange-400';
    case 'medium':
      return 'bg-warning-400';
    case 'low':
      return 'bg-success-400';
    default:
      return 'bg-slate-400';
  }
}

function severityIcon(severity: ActivityEvent['severity']) {
  switch (severity) {
    case 'critical':
      return <XOctagon className="w-4 h-4 text-danger-400 shrink-0" />;
    case 'high':
      return <ShieldAlert className="w-4 h-4 text-orange-400 shrink-0" />;
    case 'medium':
      return <AlertTriangle className="w-4 h-4 text-warning-400 shrink-0" />;
    case 'low':
      return <CheckCircle className="w-4 h-4 text-success-400 shrink-0" />;
    default:
      return <Activity className="w-4 h-4 text-slate-400 shrink-0" />;
  }
}

// ---------------------------------------------------------------------------
// Helper: trend arrow
// ---------------------------------------------------------------------------

function TrendIndicator({ trend, label }: { trend?: 'up' | 'down' | 'neutral'; label?: string }) {
  if (!trend || trend === 'neutral') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
        <Minus className="w-3 h-3" />
        {label && <span>{label}</span>}
      </span>
    );
  }
  // "down" for threats means good -- green; "up" for resolution means good -- green
  // We keep it simple: the label already carries context so we just color them positively.
  const isPositive = true; // All mock trends are favorable
  const color = isPositive ? 'text-success-400' : 'text-danger-400';
  const Icon = trend === 'up' ? TrendingUp : TrendingDown;

  return (
    <span className={`inline-flex items-center gap-1 text-xs ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {label && <span>{label}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helper: connector dot color
// ---------------------------------------------------------------------------

function connectorDotClass(status: ConnectorDot['status']): string {
  switch (status) {
    case 'connected':
      return 'bg-success-400';
    case 'warning':
      return 'bg-warning-400';
    case 'disconnected':
      return 'bg-danger-400';
    default:
      return 'bg-slate-500';
  }
}

function connectorIcon(status: ConnectorDot['status']) {
  switch (status) {
    case 'connected':
      return <Wifi className="w-3 h-3 text-success-400" />;
    case 'warning':
      return <AlertTriangle className="w-3 h-3 text-warning-400" />;
    case 'disconnected':
      return <WifiOff className="w-3 h-3 text-danger-400" />;
  }
}

// ---------------------------------------------------------------------------
// Status banner config
// ---------------------------------------------------------------------------

function statusBannerConfig(status: OverallStatus) {
  switch (status) {
    case 'all-clear':
      return {
        icon: ShieldCheck,
        title: 'All Clear',
        description:
          'Your security posture is healthy. ZoroAgent is monitoring your environment 24/7 and no action is needed right now.',
        bgGradient: 'from-success-500/20 via-success-500/5 to-transparent',
        borderColor: 'border-success-500/30',
        iconColor: 'text-success-400',
        badgeClass: 'badge badge-success',
        pulseColor: 'bg-success-400',
      };
    case 'attention':
      return {
        icon: ShieldAlert,
        title: 'Attention Needed',
        description:
          'There are a few items that may need your review. ZoroAgent is handling them, but you may want to check in.',
        bgGradient: 'from-warning-500/20 via-warning-500/5 to-transparent',
        borderColor: 'border-warning-500/30',
        iconColor: 'text-warning-400',
        badgeClass: 'badge badge-warning',
        pulseColor: 'bg-warning-400',
      };
    case 'critical':
      return {
        icon: ShieldOff,
        title: 'Critical',
        description:
          'Immediate attention is required. Critical security issues have been detected that need human review.',
        bgGradient: 'from-danger-500/20 via-danger-500/5 to-transparent',
        borderColor: 'border-danger-500/30',
        iconColor: 'text-danger-400',
        badgeClass: 'badge badge-danger',
        pulseColor: 'bg-danger-400',
      };
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function SimpleMode({ onSwitchToAdvanced }: SimpleModeProps) {
  const [scanning, setScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);

  const handleRunScan = useCallback(() => {
    setScanning(true);
    setScanComplete(false);
    setTimeout(() => {
      setScanning(false);
      setScanComplete(true);
      // Reset the "complete" badge after a few seconds
      setTimeout(() => setScanComplete(false), 4000);
    }, 3000);
  }, []);

  const banner = statusBannerConfig(MOCK_STATUS);
  const BannerIcon = banner.icon;

  const connectedCount = MOCK_CONNECTORS.filter(c => c.status === 'connected').length;
  const totalConnectors = MOCK_CONNECTORS.length;

  return (
    <div className="min-h-screen bg-slate-900 p-6 md:p-10 lg:p-12 animate-fade-in">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* ---------------------------------------------------------------- */}
        {/* Header                                                          */}
        {/* ---------------------------------------------------------------- */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">ZoroAgent</h1>
              <p className="text-sm text-slate-400">Executive Security Overview</p>
            </div>
          </div>
          <span className="badge badge-success text-xs">Simple Mode</span>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Overall Status Banner                                           */}
        {/* ---------------------------------------------------------------- */}
        <div
          className={`glass rounded-2xl p-8 bg-gradient-to-r ${banner.bgGradient} border ${banner.borderColor} animate-slide-up`}
        >
          <div className="flex items-center gap-6">
            {/* Pulsing icon */}
            <div className="relative shrink-0">
              <div
                className={`absolute inset-0 rounded-full ${banner.pulseColor} opacity-20 animate-ping-slow`}
                style={{ width: 64, height: 64 }}
              />
              <div
                className={`relative w-16 h-16 rounded-full flex items-center justify-center ${banner.pulseColor}/20 border-2 ${banner.borderColor}`}
              >
                <BannerIcon className={`w-8 h-8 ${banner.iconColor}`} />
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-3xl font-bold text-white">{banner.title}</h2>
                <span className={banner.badgeClass}>
                  {MOCK_STATUS === 'all-clear'
                    ? 'Healthy'
                    : MOCK_STATUS === 'attention'
                      ? 'Review'
                      : 'Action Required'}
                </span>
              </div>
              <p className="text-base text-slate-300 leading-relaxed max-w-2xl">
                {banner.description}
              </p>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Big Metric Cards  (2x2)                                         */}
        {/* ---------------------------------------------------------------- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {MOCK_METRICS.map((metric, idx) => (
            <div
              key={metric.label}
              className="glass rounded-2xl p-6 animate-slide-up"
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              {metric.type === 'score' ? (
                /* -- Circular Score Card -- */
                <div className="flex items-center gap-6">
                  <CircularScore value={metric.scoreValue ?? 0} />
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-white mb-1">{metric.label}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{metric.subtitle}</p>
                  </div>
                </div>
              ) : (
                /* -- Standard Metric Card -- */
                <div>
                  <p className="text-sm text-slate-400 mb-2 font-medium">{metric.label}</p>
                  <div className="flex items-end gap-3 mb-2">
                    <span className="text-4xl font-bold text-white leading-none">
                      {metric.value}
                    </span>
                    {metric.trend && (
                      <TrendIndicator trend={metric.trend} label={metric.trendLabel} />
                    )}
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">{metric.subtitle}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Recent Activity Feed                                            */}
        {/* ---------------------------------------------------------------- */}
        <div className="glass rounded-2xl p-6 animate-slide-up" style={{ animationDelay: '320ms' }}>
          <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary-400" />
            Recent Activity
          </h3>

          <div className="space-y-1">
            {MOCK_ACTIVITY.map(event => (
              <div
                key={event.id}
                className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-800/40 transition-colors group"
              >
                {/* Severity dot */}
                <div className="mt-1.5 shrink-0 flex items-center gap-2">
                  <span
                    className={`block w-2.5 h-2.5 rounded-full ${severityDotClass(event.severity)}`}
                  />
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-200 leading-relaxed">{event.message}</p>
                  <p className="text-xs text-slate-500 mt-1">{event.timestamp}</p>
                </div>

                {/* Icon */}
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  {severityIcon(event.severity)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Quick Actions                                                   */}
        {/* ---------------------------------------------------------------- */}
        <div className="glass rounded-2xl p-6 animate-slide-up" style={{ animationDelay: '400ms' }}>
          <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary-400" />
            Quick Actions
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Run Security Check */}
            <button
              onClick={handleRunScan}
              disabled={scanning}
              className={`btn flex items-center justify-center gap-2.5 py-4 text-base font-semibold rounded-xl transition-all duration-300 ${
                scanComplete ? 'btn-success' : 'btn-primary'
              } ${scanning ? 'opacity-70 cursor-wait' : ''}`}
            >
              {scanning ? (
                <>
                  <Activity className="w-5 h-5 animate-spin" />
                  Scanning...
                </>
              ) : scanComplete ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  All Clear
                </>
              ) : (
                <>
                  <PlayCircle className="w-5 h-5" />
                  Run Security Check
                </>
              )}
            </button>

            {/* View Full Dashboard */}
            <button
              onClick={onSwitchToAdvanced}
              className="btn flex items-center justify-center gap-2.5 py-4 text-base font-medium rounded-xl bg-slate-700/50 text-slate-200 hover:bg-slate-700 hover:text-white border border-slate-600/50 transition-all duration-200"
            >
              <LayoutDashboard className="w-5 h-5" />
              Full Dashboard
            </button>

            {/* Download Report */}
            <button className="btn flex items-center justify-center gap-2.5 py-4 text-base font-medium rounded-xl bg-slate-700/50 text-slate-200 hover:bg-slate-700 hover:text-white border border-slate-600/50 transition-all duration-200">
              <FileDown className="w-5 h-5" />
              Download Report
            </button>

            {/* Contact Security Team */}
            <button className="btn flex items-center justify-center gap-2.5 py-4 text-base font-medium rounded-xl bg-slate-700/50 text-slate-200 hover:bg-slate-700 hover:text-white border border-slate-600/50 transition-all duration-200">
              <Phone className="w-5 h-5" />
              Contact Team
            </button>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Connector Status                                                */}
        {/* ---------------------------------------------------------------- */}
        <div className="glass rounded-2xl p-6 animate-slide-up" style={{ animationDelay: '480ms' }}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary-400" />
              Connected Tools
            </h3>
            <span className="text-sm text-slate-400">
              <span className="text-success-400 font-semibold">{connectedCount}</span> of{' '}
              {totalConnectors} tools connected
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            {MOCK_CONNECTORS.map(connector => (
              <div
                key={connector.name}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50 hover:border-slate-600/50 transition-colors group"
                title={`${connector.name}: ${connector.status}`}
              >
                <span
                  className={`block w-2 h-2 rounded-full ${connectorDotClass(connector.status)} ${
                    connector.status === 'connected' ? 'animate-pulse-slow' : ''
                  }`}
                />
                <span className="text-sm text-slate-300 group-hover:text-white transition-colors">
                  {connector.name}
                </span>
                <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                  {connectorIcon(connector.status)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Footer note                                                     */}
        {/* ---------------------------------------------------------------- */}
        <div className="text-center pb-4">
          <p className="text-xs text-slate-500">
            Last updated just now &middot; Auto-refreshes every 60 seconds &middot;{' '}
            <button
              onClick={onSwitchToAdvanced}
              className="text-primary-400 hover:text-primary-300 underline underline-offset-2"
            >
              Switch to Advanced View
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
